import React, { useState } from "react";

import {
  DropdownField,
  FieldSpy,
  Stack,
  SubmissionForm,
  TextBadge,
  TextBadgeColor,
  useTheme
} from "@bps/fluent-ui";
import { ValidationSchema, Validator } from "@bps/validator";
import { BulkActionResponse } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  useAuthActionsRefData,
  useBulkAuthAction
} from "@libs/api/gateways/field/field-ops-gateway.hooks";

import { BulkActionType, BulkAuthActionFormValues } from "../../formTypes";
import { BulkActionFormWrapper } from "../BulkActionFormWrapper";

const validator = new Validator<BulkAuthActionFormValues>();

const schema: ValidationSchema<BulkAuthActionFormValues> = {
  selectorId: validator.string().required({ message: "Selector is required" }),
  actionType: validator
    .string()
    .required({ message: "Action Type is required" }),
  authActionType: validator
    .string()
    .required({ message: "Auth action type is required" })
};

export const BulkAuthActionForm: React.FC = () => {
  const theme = useTheme();
  const [responseMessage, setResponseMessage] = useState<
    BulkActionResponse | undefined
  >();

  const { mutateAsync: sendBulkAuthAction } = useBulkAuthAction();

  const { data: authActionRefData } = useAuthActionsRefData();

  const authActionOptions =
    authActionRefData?.map(refData => {
      return { key: refData.code, text: refData.code };
    }) ?? [];

  const onSubmit = async (formValues: BulkAuthActionFormValues) => {
    const { selectorId, authActionType } = formValues;
    if (!selectorId || !authActionType) return;

    // Submit request for bulk action
    const response = await sendBulkAuthAction({
      selectorId,
      deviceAction: authActionType
    });

    setResponseMessage(response);
  };

  return (
    <SubmissionForm
      onSubmit={onSubmit}
      initialValues={{
        actionType: BulkActionType.AuthAction,
        selectorId: undefined,
        authActionType: undefined
      }}
      validate={values => validator.validate(values, schema)}
      hideButtons
    >
      {() => (
        <BulkActionFormWrapper responseMessage={responseMessage}>
          <Stack
            verticalFill
            tokens={{ childrenGap: theme.spacing.s1 }}
            styles={{ root: { width: "30%", minWidth: 300 } }}
          >
            <DropdownField
              options={authActionOptions}
              name="authActionType"
              label="Auth Action Type"
              required
              placeholder="Please select..."
              withNoEmptyOption
            />

            <FieldSpy<string> name="authActionType">
              {value => {
                const currentAction = authActionRefData?.find(
                  val => val.code === value
                );
                if (!currentAction) return null;

                return (
                  <TextBadge badgeColor={TextBadgeColor.yellow}>
                    {currentAction?.text}
                  </TextBadge>
                );
              }}
            </FieldSpy>
          </Stack>
        </BulkActionFormWrapper>
      )}
    </SubmissionForm>
  );
};
